import { useMap } from '@vis.gl/react-google-maps';
import PropTypes from 'prop-types';
import { useEffect } from 'react';

import { DEFAULT_CENTER } from '../../constants';

/**
 * Non-visual helper that imperatively syncs the Google Maps camera with app state.
 *
 * Purpose:
 * - Keep the parent `Map` component uncontrolled (using `defaultCenter`/`defaultZoom`) while still
 *   being able to programmatically pan/zoom for specific domain events (e.g., when the
 *   active organization changes).
 * - Avoids refs; uses `useMap()` from `@vis.gl/react-google-maps` to access the map instance.
 *
 * Behavior:
 * - When `center` or `zoom` props change, calls `map.panTo(center)` and, if provided,
 *   `map.setZoom(zoom)`.
 * - Renders nothing (returns `null`).
 *
 * When to use:
 * - You generally want the map to manage its own state, but occasionally need to
 *   reposition/zoom in reaction to application state changes.
 *
 * When not to use:
 * - If you need the camera to be controlled at all times, pass `center`/`zoom` directly
 *   to the `Map` component (controlled mode) instead of using this helper.
 *
 * Requirements:
 * - Must be rendered as a descendant of `Map` so `useMap()` has access to context.
 *
 * @param {{lat: number, lng: number}} center Target coordinates to pan to.
 * @param {number} [zoom] Optional zoom level to set after panning.
 *
 * @example
 * // Uncontrolled Map with occasional programmatic repositioning
 * <Map defaultCenter={DEFAULT_CENTER} defaultZoom={MIN_ZOOM}>
 *   <MapCameraSync center={centerFromState} zoom={8} />
 * </Map>
 */
const MapCameraSync = ({ center = DEFAULT_CENTER, zoom }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !center) return;

    map.panTo(center);

    if (typeof zoom === 'number') {
      map.setZoom(zoom);
    }
  }, [map, center, zoom]);

  return null;
};

MapCameraSync.propTypes = {
  center: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  zoom: PropTypes.number,
};

export default MapCameraSync;
